import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, MoreHorizontal, Pencil, Plus, RefreshCw, Shield, Trash2, User, UserCheck, UserX } from 'lucide-react';
import { toast } from 'sonner';
import { authApi, AdminUser } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { EmptyState, PageBar, PrimaryButton, SearchInput, ToolButton } from '../../components/admin/dash/Toolbar';
import { UserPanel, UserFormValues } from '../../components/admin/users/UserPanel';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const initials = (u: AdminUser) => (u.name || u.email).substring(0, 2).toUpperCase();
const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

const AdminUsers: React.FC = () => {
  const { isAdmin, user: currentAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);

  const [panelOpen, setPanelOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [deletingUser, setDeletingUser] = useState<AdminUser | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const patch = useCallback(
    (next: Record<string, string | null>) => {
      setParams(
        (p) => {
          const out = new URLSearchParams(p);
          Object.entries(next).forEach(([k, v]) => (v === null || v === '' ? out.delete(k) : out.set(k, v)));
          return out;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await authApi.getUsers();
      setUsers(data);
    } catch (err) {
      toast.error('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) => u.name?.toLowerCase().includes(needle) || u.email.toLowerCase().includes(needle));
  }, [users, q]);

  const openAdd = () => {
    setEditingUser(null);
    setFormError(null);
    setPanelOpen(true);
  };

  const openEdit = (user: AdminUser) => {
    setEditingUser(user);
    setFormError(null);
    setPanelOpen(true);
  };

  const submitPanel = async (values: UserFormValues) => {
    setFormError(null);
    if (editingUser) {
      setSaving(true);
      try {
        const ok = await authApi.updateUser(editingUser.id, { name: values.name, role: values.role });
        if (!ok) throw new Error('Failed to update user');
        toast.success(`Saved “${values.name || editingUser.email}”`);
        setPanelOpen(false);
        loadUsers();
      } catch (err: any) {
        setFormError(err?.message || 'An error occurred');
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!values.email || !values.password) {
      setFormError('Email and password are required');
      return;
    }
    setSaving(true);
    try {
      const result = await authApi.createUser(values.email, values.password, values.name || undefined, values.role);
      if (!result.success) throw new Error(result.error || 'Failed to create user');
      toast.success(`Created “${values.name || values.email}”`);
      setPanelOpen(false);
      loadUsers();
    } catch (err: any) {
      setFormError(err?.message || 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user: AdminUser) => {
    try {
      const ok = await authApi.updateUser(user.id, { isActive: !user.isActive });
      if (!ok) throw new Error('Failed to update status');
      loadUsers();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update status');
    }
  };

  const confirmDelete = async () => {
    if (!deletingUser) return;
    setDeleting(true);
    try {
      const ok = await authApi.deleteUser(deletingUser.id);
      if (!ok) throw new Error('Failed to delete user');
      toast.success(`Deleted “${deletingUser.name || deletingUser.email}”`);
      setDeletingUser(null);
      loadUsers();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 bg-[color:var(--oa-card)] p-8 font-poppins text-center">
        <p className="font-montserrat text-[15px] font-bold text-[color:var(--oa-bad)]">Access denied</p>
        <p className="text-[13px] text-[color:var(--oa-muted)]">You need admin privileges to access this page.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col font-poppins text-[color:var(--oa-text)] antialiased">
      <PageBar
        title="Users"
        meta={loading ? 'Loading…' : `${users.length} user${users.length === 1 ? '' : 's'}`}
        actions={
          <>
            <ToolButton onClick={loadUsers} disabled={loading} title="Reload">
              <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            </ToolButton>
            <PrimaryButton onClick={openAdd}>
              <Plus className="h-4 w-4" />
              Add user
            </PrimaryButton>
          </>
        }
      >
        <SearchInput value={q} onChange={(v) => patch({ q: v || null })} placeholder="Search name or email" className="ml-3 hidden md:block" />
      </PageBar>

      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full min-w-[720px] border-separate border-spacing-0 text-[13px] max-[700px]:min-w-0 max-[700px]:table-fixed">
          <thead className="sticky top-0 z-[2]">
            <tr>
              <th className="h-9 border-b border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)] px-4 text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-[color:var(--oa-muted)]">User</th>
              <th className="oa-hide-phone h-9 w-[120px] border-b border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)] px-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-[color:var(--oa-muted)]">Role</th>
              <th className="h-9 w-[120px] border-b border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)] px-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-[color:var(--oa-muted)]">Status</th>
              <th className="oa-hide-phone h-9 w-[130px] border-b border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)] px-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-[color:var(--oa-muted)]">Created</th>
              <th className="h-9 w-[52px] border-b border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)]" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="h-14">
                  {Array.from({ length: 5 }).map((__, j) => (
                    <td key={j} className="border-b border-[color:var(--oa-line-2)] px-2.5">
                      <div className="h-3 animate-pulse rounded bg-[color:var(--oa-raised-2)]" style={{ width: j === 0 ? '60%' : '50%' }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyState icon={User} title={q ? 'No users match' : 'No users yet'} hint={q ? 'Loosen the search.' : 'Add the first user.'}>
                    {!q ? (
                      <PrimaryButton onClick={openAdd}>
                        <Plus className="h-4 w-4" />
                        Add user
                      </PrimaryButton>
                    ) : null}
                  </EmptyState>
                </td>
              </tr>
            ) : (
              visible.map((user, i) => (
                <tr
                  key={user.id}
                  className="group/row h-14"
                  style={{ animation: 'oa-rowin .6s var(--oa-ease) both', animationDelay: `${i * 45}ms` }}
                >
                  <td className="border-b border-[color:var(--oa-line-2)] py-2 pl-4 pr-2.5 group-hover/row:bg-[color:var(--oa-accent-soft)]">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="oa-avatar">{initials(user)}</span>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-[color:var(--oa-text)]">
                          {user.name || 'Unnamed'}
                          {user.id === currentAdmin?.id ? <span className="ml-1.5 text-[11px] font-normal text-[color:var(--oa-faint)]">(You)</span> : null}
                        </p>
                        <p className="flex items-center gap-1 truncate text-[12px] text-[color:var(--oa-muted)]">
                          <Mail className="h-3 w-3 shrink-0" />
                          {user.email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="oa-hide-phone border-b border-[color:var(--oa-line-2)] px-2.5 group-hover/row:bg-[color:var(--oa-accent-soft)]">
                    <span className={`oa-chip ${user.role === 'admin' ? 'is-good' : 'is-plain'} inline-flex items-center gap-1`}>
                      <Shield className="h-3 w-3" />
                      {user.role}
                    </span>
                  </td>
                  <td className="border-b border-[color:var(--oa-line-2)] px-2.5 group-hover/row:bg-[color:var(--oa-accent-soft)]">
                    <button
                      type="button"
                      onClick={() => handleToggleActive(user)}
                      disabled={user.id === currentAdmin?.id}
                      className={`oa-chip ${user.isActive ? 'is-good' : 'is-bad'} inline-flex items-center gap-1 disabled:cursor-not-allowed disabled:opacity-50`}
                    >
                      {user.isActive ? <UserCheck className="h-3 w-3" /> : <UserX className="h-3 w-3" />}
                      {user.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="oa-hide-phone border-b border-[color:var(--oa-line-2)] px-2.5 text-[12px] tabular-nums text-[color:var(--oa-muted)] group-hover/row:bg-[color:var(--oa-accent-soft)]">
                    {formatDate(user.createdAt)}
                  </td>
                  <td className="border-b border-[color:var(--oa-line-2)] px-2 text-right group-hover/row:bg-[color:var(--oa-accent-soft)]">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" aria-label={`Actions for ${user.email}`} className="grid h-7 w-7 place-items-center rounded-md text-[color:var(--oa-muted)] hover:bg-[color:var(--oa-raised-2)] hover:text-[color:var(--oa-text)] data-[state=open]:bg-[color:var(--oa-raised-2)]">
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44 rounded-xl">
                        <DropdownMenuItem onSelect={() => openEdit(user)} className="gap-2">
                          <Pencil className="h-3.5 w-3.5" /> Edit
                        </DropdownMenuItem>
                        {user.id !== currentAdmin?.id ? (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onSelect={() => setDeletingUser(user)} className="gap-2 text-[color:var(--oa-bad)] focus:text-[color:var(--oa-bad)]">
                              <Trash2 className="h-3.5 w-3.5" /> Delete
                            </DropdownMenuItem>
                          </>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <UserPanel
        open={panelOpen}
        onOpenChange={setPanelOpen}
        editingUser={editingUser}
        isSelf={editingUser?.id === currentAdmin?.id}
        saving={saving}
        error={formError}
        onSubmit={submitPanel}
      />

      <AlertDialog open={!!deletingUser} onOpenChange={(o) => !o && !deleting && setDeletingUser(null)}>
        <AlertDialogContent className="rounded-xl font-poppins">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-montserrat text-[16px] font-extrabold text-[color:var(--oa-text)]">Delete this user?</AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] text-[color:var(--oa-muted)]">
              “{deletingUser?.name || deletingUser?.email}” will lose access immediately. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} className="h-8 rounded-md text-[12.5px]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              disabled={deleting}
              className="h-8 rounded-md bg-[color:var(--oa-bad)] text-[12.5px] text-white hover:bg-[color:var(--oa-bad)]"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminUsers;
